/* eslint-disable complexity */
import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {GetSearchResultsStreamResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/search/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useTrackFeature} from '@aboutyou/tracking';
import React, {memo, useCallback} from 'react';
import shallowEqual from 'shallowequal';
import {
  ProductTile,
  ProductTileProps,
} from '../../../features/ProductTile/ProductTile';
import {StreamProductTileWrapper} from '../../../features/ProductTile/StreamProductTileWrapper';
import {ShallowRenderingState} from '../../../features/ProductTile/utils';

export interface SearchResultsStreamTileListProps {
  gridColumns: number | undefined;
  shallowRenderingState?: ShallowRenderingState;
  onClickAddCta?: ({
    productId,
    filteredSizeId,
    couponPromotionId,
    locationId,
  }: {
    productId: number;
    filteredSizeId?: number;
    couponPromotionId?: string;
    locationId?: string;
  }) => void;
  addCtaTitle?: string;
  productTileGalleryMode?: 'hover' | 'always';
  interactiveHoverMode?: boolean;
  enableImageGalleryAnimation?: boolean;
  selectedCoupon?: string;
  ProductTileComponent?: React.FC<ProductTileProps>;
  itemsPreferences?: ProductTilePreferences;
}

const SearchResultsStreamTileList: React.FC<
  SearchResultsStreamTileListProps &
    Omit<GetSearchResultsStreamResponse, 'sections'> & {
      items: GetSearchResultsStreamResponse['sections'];
    }
> = ({
  items,
  gridColumns,
  shallowRenderingState,
  onClickAddCta,
  addCtaTitle,
  productTileGalleryMode,
  interactiveHoverMode,
  enableImageGalleryAnimation,
  selectedCoupon,
  ProductTileComponent = ProductTile,
}) => {
  const trackFeature = useTrackFeature();

  const handleClickAddCta = useCallback(
    ({
        filteredSizeId,
        productId,
        couponPromotionId,
      }: {
        filteredSizeId?: number;
        productId: number;
        couponPromotionId?: string;
      }) =>
      () => {
        onClickAddCta?.({
          productId,
          filteredSizeId,
          couponPromotionId,
          locationId: 'product_stream',
        });
        trackFeature({
          eventType: 'click',
          label: `add_to_basket_stream|${productId}`,
          name: 'add_to_basket_stream',
        });
      },
    [onClickAddCta],
  );

  const handleColorClick = useCallback((productId: number) => {
    if (interactiveHoverMode) {
      trackFeature({
        eventType: 'click',
        label: `search_results_page_color_picker`,
      });
      onClickAddCta?.({
        productId,
        locationId: 'product_stream',
      });
    }
  }, []);

  const {t} = useTranslation();

  return (
    <>
      {items.map((tile, index) => {
        const streamPosition = index;

        const productTileTrackingData = {
          recoType: 'product_stream',
        };

        switch (tile.type?.$case) {
          case 'productSection': {
            const productTile = tile.type.productSection.productTile;

            const productId = productTile.productId;

            return (
              <StreamProductTileWrapper
                key={`productSection-${productId}`}
                productId={productId}
                shallowRenderingState={shallowRenderingState}
                renderTile={({isInViewOnRemount, tileRef}) => {
                  return (
                    <ProductTileComponent
                      category={undefined}
                      isInViewOnRemount={isInViewOnRemount}
                      tileRef={
                        tileRef as React.RefObject<HTMLDivElement | null>
                      }
                      tile={productTile}
                      importance={'auto'}
                      streamPosition={streamPosition}
                      trackingData={productTileTrackingData}
                      enhanceSpeed={true}
                      onClickAddCta={
                        onClickAddCta
                          ? handleClickAddCta({
                              productId,
                              filteredSizeId: productTile.selectedSizeId,
                              couponPromotionId: productTile.couponPromotionId,
                            })
                          : undefined
                      }
                      gridColumns={gridColumns}
                      addCtaLabel={addCtaTitle ?? t('Add to basket')}
                      onColorClick={handleColorClick}
                      productTileGallery={productTileGalleryMode === 'always'}
                      selectedCoupon={selectedCoupon}
                      enableImageGalleryAnimation={
                        index === 0 && enableImageGalleryAnimation
                      }
                    />
                  );
                }}
              />
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
};

const MemoizedStreamTileList = memo(SearchResultsStreamTileList, (a, b) => {
  if ('itemsPreferences' in a && 'itemsPreferences' in b) {
    const {itemsPreferences: itemsPreferencesA, ...restA} = a;
    const {itemsPreferences: itemsPreferencesB, ...restB} = b;
    return (
      shallowEqual(restA, restB) &&
      shallowEqual(itemsPreferencesA, itemsPreferencesB)
    );
  }

  return shallowEqual(a, b);
});

export {MemoizedStreamTileList as SearchResultsStreamTileList};
